import { useEffect, useRef, useState, type FormEvent } from "react";
import { CheckCircle2, Download, Eye, EyeOff, XCircle } from "lucide-react";

import { apiUrl } from "../platform";
import { supabase } from "../supabase";
import { FullBrandLogo } from "./AppShell";
import { AppLink, navigate } from "./router";

type AuthMode = "login" | "signup" | "forgot" | "reset";

export function AuthPage({ mode }: { mode: AuthMode }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const messageRef = useRef<HTMLDivElement | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const notice = error ? errorRef.current : message ? messageRef.current : null;
    if (!notice) return;
    notice.scrollIntoView({ block: "center", behavior: "smooth" });
    notice.focus({ preventScroll: true });
  }, [error, message]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setMessage("");
    const data = new FormData(event.currentTarget);
    const email = String(data.get("email") ?? "").trim();
    const password = String(data.get("password") ?? "");
    const displayName = String(data.get("displayName") ?? "").trim();
    setBusy(true);
    try {
      if (mode === "login") {
        const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
        if (authError) throw authError;
        navigate("/dashboard", true);
      } else if (mode === "signup") {
        await requestAuthEmail("/api/auth/signup", {
          email,
          password,
          displayName,
          requestPatientBetaAccess: data.get("requestPatientBetaAccess") === "on",
        });
        const nextStep = "Check your email to finish setting up Precise Plate. If this email is new to Precise Plate, a confirmation link has been sent to your inbox. Open it, select Confirm email address, then return here and sign in. If you already used this email, sign in or reset your password.";
        setMessage(nextStep);
      } else if (mode === "forgot") {
        await requestAuthEmail("/api/auth/password-reset", { email });
        setMessage("Password reset instructions have been sent if that email has a Precise Plate account.");
      } else {
        if (password.length < 8) throw new Error("Use at least 8 characters for your new password.");
        const { error: authError } = await supabase.auth.updateUser({ password });
        if (authError) throw authError;
        setMessage("Your password has been updated. You can continue to Precise Plate.");
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The request could not be completed.");
    } finally {
      setBusy(false);
    }
  }

  const title = mode === "login" ? "Sign in to Precise Plate" : mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Choose a new password";
  const actionLabel = mode === "login" ? "Sign in" : title;
  const description = mode === "login"
    ? "Sign in to your meal plans, calendar, and saved meals."
    : mode === "signup"
      ? "Set up a private Precise Plate account."
      : mode === "forgot"
        ? "Enter your email and we will send a secure reset link."
        : "Enter a new password for your account.";

  return (
    <main className="auth-shell">
      <section className="auth-panel">
        <div className="auth-brand-side">
          <AppLink href="/" className="auth-brand-full" aria-label="Precise Plate home">
            <FullBrandLogo className="auth-brand-logo" />
          </AppLink>
        </div>
        <div className="auth-form-column">
          <div className="auth-copy"><h1>{title}</h1><p>{description}</p></div>
          {error ? <div ref={errorRef} className="notice error" role="alert" tabIndex={-1}><XCircle size={20} /><span>{error}</span></div> : null}
          {message ? <div ref={messageRef} className="notice success" role="status" tabIndex={-1}><CheckCircle2 size={20} /><span>{message}</span></div> : null}
          <form onSubmit={submit} className="form-stack">
            {mode === "signup" ? <Field label="Name" name="displayName" autoComplete="name" required /> : null}
            {mode !== "reset" ? <Field label="Email" name="email" type="email" autoComplete="email" required /> : null}
            {mode === "login" || mode === "signup" || mode === "reset" ? (
              <PasswordField label={mode === "reset" ? "New password" : "Password"} name="password" autoComplete={mode === "login" ? "current-password" : "new-password"} minLength={8} required />
            ) : null}
            {mode === "signup" ? <div><label className="choice legal-choice"><input type="checkbox" name="requestPatientBetaAccess" aria-describedby="patient-beta-help" /><span>Request patient beta access (optional)</span></label><p id="patient-beta-help" className="muted">For Dr. Allen&apos;s clinic patients. Your request and account name/email will be shown to the administrator for approval of free Household access. Confirm your email after signup. You can use the Free plan while awaiting approval.</p></div> : null}
            {mode === "signup" ? <div className="signup-consent"><label className="choice legal-choice"><input type="checkbox" required /><span>I agree to the <AppLink href="/terms">Terms</AppLink> and acknowledge the <AppLink href="/privacy">Privacy Policy</AppLink>.</span></label><label className="choice legal-choice"><input type="checkbox" required /><span>I understand that Precise Plate is not medical care, as explained in the <AppLink href="/medical-disclaimer">Medical Disclaimer</AppLink>.</span></label></div> : null}
            <button className="button primary wide" disabled={busy} type="submit">
              {busy ? "Please wait..." : actionLabel}
            </button>
          </form>
          <div className="auth-links">
            {mode === "login" ? <><AppLink href="/forgot-password">Forgot password?</AppLink><AppLink href="/signup">Create account</AppLink></> : null}
            {mode === "signup" ? <><AppLink href="/login">Sign in</AppLink><AppLink href="/forgot-password">Reset password</AppLink></> : null}
            {mode !== "login" && mode !== "signup" ? <AppLink href="/login">Back to sign in</AppLink> : null}
            {mode === "reset" && message ? <AppLink href="/dashboard">Continue to dashboard</AppLink> : null}
          </div>
          <AppLink href="/install" className="auth-install-link"><Download size={17} />Install Precise Plate</AppLink>
          <div className="auth-legal-links"><AppLink href="/privacy">Privacy</AppLink><AppLink href="/terms">Terms</AppLink><AppLink href="/medical-disclaimer">Medical disclaimer</AppLink><AppLink href="/support">Support</AppLink></div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, name, type = "text", ...props }: React.InputHTMLAttributes<HTMLInputElement> & { label: string; name: string }) {
  return <label className="field"><span>{label}</span><input name={name} type={type} {...props} /></label>;
}

function PasswordField({ label, name, ...props }: React.InputHTMLAttributes<HTMLInputElement> & { label: string; name: string }) {
  const [visible, setVisible] = useState(false);
  const actionLabel = visible ? "Hide password" : "Show password";
  const inputId = props.id ?? `${name}-input`;

  return (
    <div className="field">
      <label htmlFor={inputId}>{label}</label>
      <div className="password-input-wrap">
        <input id={inputId} name={name} type={visible ? "text" : "password"} {...props} />
        <button
          className="password-visibility-button"
          type="button"
          aria-label={actionLabel}
          title={actionLabel}
          aria-pressed={visible}
          onClick={() => setVisible((current) => !current)}
        >
          {visible ? <EyeOff size={19} /> : <Eye size={19} />}
        </button>
      </div>
    </div>
  );
}

async function requestAuthEmail(path: string, body: Record<string, unknown>): Promise<void> {
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error("Precise Plate could not reach the signup service. Check your connection, reload the app, and try again.");
  }
  const result = await response.json().catch(() => ({})) as { error?: unknown };
  if (!response.ok) {
    throw new Error(typeof result.error === "string" ? result.error : "The email could not be sent. Please try again.");
  }
}
